import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { Locale } from "@/Common/Models/Localization/Locale";
import { ObstetricalCounters } from "@/Patient/Models/MedicalSummary/ObstetricalSummary";

interface IObstetricalCountersProps {
    containerId: string;
    readonly?: boolean;
    renderHeaders?: boolean;
    obstetricalCounters: ObstetricalCounters
    onChange: (counters: ObstetricalCounters) => void;
    onBlur?: () => void;
    includeFields?: ObstetricalCounter[];
    longLabel?: boolean;
    largeFirstLetterLabels?: boolean;
    additionalFields?: { value: number, headerLabel: string, sideLabel: string, onChange: (value: number) => void, onBlur?: () => void }[]
    alternateLabels?: { top?: { [index: string]: string }, side?: { [index: string]: string } }
    locale?: Locale;
    showAlternateAbortionCounter?: boolean;
}

class ObstetricalCountersComponent extends BaseReactComponent<IObstetricalCountersProps> {
    // Those are for large first letter only. AbortionSpontaneous is not part of it.
    private readonly countersForGtpalAbbrev = [
        ObstetricalCounter.Gravida,
        ObstetricalCounter.Term,
        ObstetricalCounter.Premature,
        ObstetricalCounter.Abortion,
        ObstetricalCounter.AbortionInduced,
        ObstetricalCounter.Live,
        ObstetricalCounter.Stillbirth
    ];

    public render(): JSX.Element {
        const obstetricalCounters = this.props.obstetricalCounters;

        return (
            <div className={`flex-container align-center ${this.props.containerId}`}>
                {this.renderDefaultCounter(obstetricalCounters.Gravida, value => this.mutateCounters(s => s.Gravida = value), ObstetricalCounter.Gravida)}
                {this.renderDefaultCounter(obstetricalCounters.Term, value => this.mutateCounters(s => s.Term = value), ObstetricalCounter.Term)}
                {this.renderDefaultCounter(obstetricalCounters.Premature, value => this.mutateCounters(s => s.Premature = value), ObstetricalCounter.Premature)}
                {this.renderAbortionCounter()}
                {this.renderDefaultCounter(obstetricalCounters.Live, value => this.mutateCounters(s => s.Live = value), ObstetricalCounter.Live)}
                {this.renderDefaultCounter(obstetricalCounters.Stillbirth, value => this.mutateCounters(s => s.Stillbirth = value), ObstetricalCounter.Stillbirth)}
                {this.props.additionalFields?.map(p => this.renderCounter(p.value, value => p.onChange(value), () => p.onBlur?.(), p.headerLabel, p.sideLabel, `${p.headerLabel}${p.sideLabel}`))}
            </div>
        );
    }

    private renderAbortionCounter(): React.ReactNode {
        if (this.props.showAlternateAbortionCounter) {
            const longLabel = this.props.longLabel;
            const largeFirstLetter = this.props.largeFirstLetterLabels;
            const nbsp = "\u00A0";

            const node = [
                this.renderCounter(
                    this.props.obstetricalCounters.AbortionInduced,
                    value => this.mutateCounters(s => {
                        s.AbortionInduced = value;
                        s.Abortion = value + (s.AbortionSpontaneous ?? 0);
                    }),
                    () => this.onBlur(),
                    <div style={{ marginTop: "-20px" }}>{this.getLabelString("ObstetricalFile_InducedAbortionLabel")}</div>,
                    <div className="flex-display">
                        <span>
                            {this.getLabelString(
                                this.props.alternateLabels?.side?.[ObstetricalCounter.AbortionInduced]
                                    ?? `Abortus${longLabel ? "" : "Counter"}Label`,
                                ObstetricalCounter.AbortionInduced
                            )}
                        </span>
                        {longLabel && largeFirstLetter
                            ? <span className="gtpal-large-first-letter-rest">{nbsp}({cacheStore.GetString("InducedLabel", null, this.props.locale)}</span>
                            : <span style={{ marginTop: "-2px" }}>(</span>
                        }
                    </div>,
                    "AbortionInduced",
                    ObstetricalCounter.AbortionInduced
                ),
                this.renderCounter(
                    this.props.obstetricalCounters.AbortionSpontaneous,
                    value => this.mutateCounters(s => {
                        s.AbortionSpontaneous = value;
                        s.Abortion = value + (s.AbortionInduced ?? 0);
                    }),
                    () => this.onBlur(),
                    <div style={{ marginTop: "-20px" }}>{this.getLabelString("ObstetricalFile_SpontaneousAbortionLabel")}</div>,
                    longLabel && largeFirstLetter
                        ? <div className="gtpal-large-first-letter-rest">{cacheStore.GetString("SpontaneousLabel", null, this.props.locale)}</div>
                        : "",
                    "AbortionSpontaneous",
                    null,
                    longLabel && largeFirstLetter ? <div className="gtpal-large-first-letter-rest margin-xs-right">)</div> : null
                )
            ];

            if (!longLabel || !largeFirstLetter) {
                node.push(
                    <div className="obstetrical-summary-counter" key={`${this.props.containerId}-closing-parenthesis`}>
                        <div className="flex-container align-center gtpals-container" style={{ marginTop: this.props.renderHeaders ? "-8px" : "auto" }}>
                            <span className={`gtpals-letters ${!this.props.renderHeaders ? "no-header" : ""}`}>
                                <div style={{ marginTop: "-4px" }}>)</div>
                            </span>
                        </div>
                    </div>
                );
            }

            return node;
        }

        return this.renderDefaultCounter(this.props.obstetricalCounters.Abortion, value => this.mutateCounters(s => s.Abortion = value), ObstetricalCounter.Abortion);
    }

    private renderDefaultCounter(
        value: number,
        onChange: (value: number) => void,
        counterType: ObstetricalCounter
    ): JSX.Element {
        if (this.props.includeFields?.any() && !this.props.includeFields.contains(counterType))
            return null;

        const topLabel = this.props.alternateLabels?.top?.[counterType] ?? `${ObstetricalCounter[counterType]}Label`;
        const sideLabel = this.props.alternateLabels?.side?.[counterType] ?? `${ObstetricalCounter[counterType]}${!this.props.longLabel ? "Counter" : ""}Label`;
        return this.renderCounter(
            value,
            onChange,
            () => this.onBlur(),
            topLabel,
            sideLabel,
            `${topLabel}${sideLabel}`,
            counterType
        );
    }

    private renderCounter(
        value: number,
        onChange: (value: number) => void,
        onBlur: () => void,
        headerLabel: React.ReactNode,
        sideLabel: React.ReactNode,
        id: string,
        counterType?: ObstetricalCounter,
        labelAfterTextbox?: React.ReactNode
    ): JSX.Element {
        return (
            <div className="obstetrical-summary-counter" key={`${this.props.containerId}${id}`}>
                <div className="flex-container align-center gtpals-container" style={{ marginTop: this.props.renderHeaders ? "-8px" : "auto" }}>
                    <span className={`gtpals-letters ${!this.props.renderHeaders ? "no-header" : ""}`}>
                        {this.getLabelString(sideLabel, counterType)}
                    </span>
                    <div className="textbox-gtpals-letters-container">
                        {this.props.renderHeaders && <div className="gtpals-header">{this.getLabelString(headerLabel)}</div>}
                        {this.props.readonly
                            ? <span className="textbox-gtpals-letters read-only">{value?.toString()}</span>
                            : (
                                <TextboxControl
                                    value={value?.toString()}
                                    onChange={value => onChange(value ? parseInt(value) : null)}
                                    onBlur={() => onBlur?.()}
                                    className="textbox-gtpals-letters"
                                    maxLength={2}
                                    isInteger
                                />
                            )
                        }
                    </div>
                    {labelAfterTextbox}
                </div>
            </div>
        );
    }

    private getLabelString(label: React.ReactNode, counterTypeForLargeFirstLetter?: ObstetricalCounter): React.ReactNode {
        if (typeof label === "string") {
            const str = cacheStore.GetString(label, null, this.props.locale);

            if (this.props.longLabel
                && this.props.largeFirstLetterLabels
                && this.countersForGtpalAbbrev.contains(counterTypeForLargeFirstLetter)
            ) {
                return (
                    <span className="label-with-prefix">
                        <h4 className="gtpal-large-first-letter">{str?.[0]}</h4>
                        <span className="gtpal-large-first-letter-rest">{str?.slice(1)}</span>
                    </span>
                );
            }
            else
                return str;
        }
        else
            return label;
    }

    private mutateCounters(mutation: (state: ObstetricalCounters) => void): void {
        const counters = { ...this.props.obstetricalCounters };
        mutation(counters);
        this.props.onChange(counters);
    }

    private onBlur(): void {
        this.props.onBlur?.();
    }
}

enum ObstetricalCounter {
    Gravida = "Gravida",
    Term = "Term",
    Premature = "Premature",
    Abortion = "Abortion",
    Live = "Live",
    Stillbirth = "Stillbirth",
    // British Columbia Specific Counters
    AbortionInduced = "AbortionInduced",
    AbortionSpontaneous = "AbortionSpontaneous"
}

export { ObstetricalCounter, ObstetricalCountersComponent };
